import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError, api, type MissionChoice } from "../../api/client";
import type { CorridorModel, TacticalGraphics } from "../../api/types";
import { lang, t, tc, tServer } from "../../i18n/i18n";
import { unitMissionName } from "../../i18n/vocabulary";
import { missionChoiceNotes } from "../../panels/missionMenuModel";
import { useUi } from "../../state/ui";
import {
  type DossierWrite,
  dossierWrite,
  graphicsOf,
  placementOf,
  recordWrite,
  undoAction,
  useDossierHistory,
  writeFailure,
} from "../dossierWrites";
import { history as mapHistory, isConflict, sameState, useHistoryState } from "../history";
import { orderActions, withOrders } from "../orderSync";
import { graphicsCount, handUnits, orientGraphics, orientState } from "./orientDeployment";
import {
  delta,
  directionName,
  fitFor,
  formatBearing,
  normalizeDeg,
  orientationModel,
  rumb16,
  shiftDirection,
  snapToTemplate,
  storedOrientation,
  transformOf,
} from "./orientation";
import { measureWritesBusy, useMeasureWriteState } from "./useMeasureWrites";
import { showPropagation } from "../units/showPropagation";
import { useDossierLocked } from "../../sharing/access";

/** Avis du serveur après `DELETE /corridor` (routes/deployment.py, `CORRIDOR_RESET`) : il redit le texte de l'avis
 *  « Fuseau automatique rétabli », il n'est donc pas repris dans son détail. */
export const CORRIDOR_RESET_NOTICE = "Fuseau automatique et LIMA d'avant la retouche rétablis";

/** Écritures de la manœuvre libre (docs/14 §2.2 à §2.5, §4.1) : fuseau personnalisé (état complet), retour au
 *  fuseau automatique, mission choisie d'une unité à un temps. Comme les déplacements, elles passent par la portée
 *  du dossier et s'inscrivent dans le canal « dispositif » de l'historique de la carte : ⌘Z, ou « Annuler » de
 *  l'avis, les défait par `PUT /placement` (instantané normalisé). Chaque mutation renvoie le `DossierWrite`
 *  (réponse du serveur, déploiement d'avant, instantanés) : `mutateAsync` permet d'enchaîner (sortie du mode
 *  édition, transition du symbole). Les lignes de la ZO (docs/15 §4.3 à §4.5) ont leur écriture commune à la carte,
 *  à la fiche et au panneau (`useMeasureWrites`) ; elle compte dans `busy`. */

export interface CorridorWrite {
  /** Modèle complet (`deployment.corridor.model` retouché par `corridorEdit`). */
  corridor: CorridorModel;
  /** Action inscrite dans l'historique, en minuscule et déjà traduite ; défaut : « retouche du fuseau ». */
  label?: string;
  /** Avis (défaut : oui). false : ni l'avis de 10 s après l'enregistrement, ni l'avis d'échec — sauf le conflit
   *  (412 / 409 : historique remis à zéro, dossier rechargé, toujours signalé) ; le mode édition traite lui-même
   *  le refus (422 : segment fautif, règle, retour animé, §2.11) par le rejet de `mutateAsync`. */
  notify?: boolean;
}

/** Sens de la manœuvre à appliquer (docs/14 §2.14) : azimut visé, et graphiques tactiques tournés avec lui. */
export interface OrientationWrite {
  bearing: number;
  graphics: boolean;
}

/** Résultat de l'orientation : écriture du dispositif, puis, s'il y a lieu, celle des graphiques (ou son refus). */
export interface OrientationResult {
  write: DossierWrite;
  graphics: { write: DossierWrite | null; error: unknown; rotated: TacticalGraphics | null };
  bearing: number;
  template: number;
  shift: [number, number];
  custom: boolean;
  hand: number;
}

/** Cap et rumb d'un azimut, dans la langue de l'interface (« 045° (NE) »). */
export function capLabel(deg: number): string {
  return `${formatBearing(deg)} (${rumb16(deg, lang() === "en" ? "en" : "fr")})`;
}

/** Phrase du décalage automatique (« Décalé de 1,2 km vers le nord-est pour tenir dans le camp ») ; vide sous 50 m. */
export function shiftPhrase(shift: readonly [number, number]): string {
  const found = shiftDirection(shift);
  if (!found) return "";
  const km = (found.metres / 1000).toLocaleString(lang() === "en" ? "en-GB" : "fr-FR", { maximumFractionDigits: 1 });
  return t("Décalé de {km} km vers {direction} pour tenir dans le camp", { km, direction: towards(found.deg) });
}

/** Direction d'un décalage, après « vers » : « le nord », « le nord-est », « l'est »… (« north »… en anglais). */
export function towards(deg: number): string {
  switch (directionName(deg, "fr")) {
    case "nord-est":
      return tc("vers", "le nord-est");
    case "est":
      return tc("vers", "l'est");
    case "sud-est":
      return tc("vers", "le sud-est");
    case "sud":
      return tc("vers", "le sud");
    case "sud-ouest":
      return tc("vers", "le sud-ouest");
    case "ouest":
      return tc("vers", "l'ouest");
    case "nord-ouest":
      return tc("vers", "le nord-ouest");
    default:
      return tc("vers", "le nord");
  }
}

/** Écriture de l'orientation (§2.6) : `PUT /placement` (nouveau sens, positions posées à la main et fuseau retouché
 *  tournés par M), puis `PUT /graphics` (graphiques tournés) ; le dispositif d'abord. */
async function writeOrientation(queryClient: QueryClient, input: OrientationWrite): Promise<OrientationResult> {
  const notSaved = t("Enregistrez le dossier pour orienter la manœuvre.");
  let rotated: TacticalGraphics | null = null;
  let plan: Omit<OrientationResult, "write" | "graphics"> | null = null;
  const write = await dossierWrite(
    queryClient,
    (target, opo, current) => {
      const model = orientationModel(current.corridor?.orientation);
      if (!model) throw new ApiError(422, t("Le sens de la manœuvre ne se règle pas sur ce terrain"));
      const bearing = snapToTemplate(normalizeDeg(input.bearing), model.template);
      const dst = fitFor(model, bearing);
      const m = transformOf(model.current, dst);
      const state = placementOf(current);
      const step = delta(dst) - delta(model.current);
      const graphics = graphicsOf(current);
      rotated = input.graphics && graphicsCount(graphics) > 0 ? orientGraphics(graphics, m, step) : null;
      plan = {
        bearing: dst.bearing,
        template: model.template,
        shift: dst.shift,
        custom: Boolean(state.corridor),
        hand: handUnits(state),
      };
      return api.setPlacement(target, orientState(state, m, storedOrientation(bearing, model.template)), opo);
    },
    { notSaved, channel: "dispositif" },
  );
  let graphicsWrite: DossierWrite | null = null;
  let error: unknown = null;
  const toRotate: TacticalGraphics | null = rotated;
  if (toRotate) {
    try {
      graphicsWrite = await dossierWrite(queryClient, (target, opo) => api.setGraphics(target, toRotate, opo), {
        notSaved,
        channel: "graphiques",
        orders: false,
      });
    } catch (caught) {
      error = caught;
    }
  }
  return { write, graphics: { write: graphicsWrite, error, rotated: toRotate }, ...plan! };
}

export function useManoeuvreWrites() {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : aucune écriture, l'appui désigne le bandeau.
  const locked = useDossierLocked();
  const queryClient = useQueryClient();
  const params = useUi((s) => s.deploymentParams);
  const notify = useUi((s) => s.notify);
  const historyBusy = useHistoryState((s) => s.busy);
  const measureBusy = useMeasureWriteState(measureWritesBusy);
  useDossierHistory();

  const setCorridor = useMutation({
    mutationFn: (input: CorridorWrite) =>
      dossierWrite(queryClient, (target) => api.setCorridor(target, input.corridor), {
        notSaved: t("Enregistrez le dossier pour modifier le fuseau."),
        channel: "dispositif",
      }),
    onSuccess: (write, input) => {
      const recorded = recordWrite(write, input.label ?? t("retouche du fuseau"));
      if (input.notify === false) return;
      const warnings = (write.result.corridor?.warnings ?? []).map((w) => tServer(w)).join(" · ");
      notify({
        text: t("Fuseau modifié"),
        detail: withOrders(warnings || undefined, write.result),
        tone: warnings ? "warn" : "ok",
        ...orderActions(undoAction(recorded), write.result),
      });
    },
    onError: (error, input) => {
      if (input.notify === false && !isConflict(error)) return;
      writeFailure(queryClient, error, t("Fuseau non enregistré"));
    },
  });

  // « Revenir au fuseau automatique » (camp : LIMA glissées d'avant la retouche rétablies) ou « au fuseau de la
  // zone » (zone libre) ; la confirmation (`ConfirmDialog`) est à la charge de l'appelant.
  const resetCorridor = useMutation({
    mutationFn: () =>
      dossierWrite(queryClient, (target) => api.resetCorridor(target), {
        notSaved: t("Dossier non enregistré"),
        channel: "dispositif",
      }),
    onSuccess: (write) => {
      const zone = write.previous.placement?.corridor?.origin === "free";
      const recorded = recordWrite(write, zone ? t("retour au fuseau du terrain") : t("retour au fuseau automatique"));
      // Le sens de la manœuvre est gardé (docs/14 §2.14) : l'avis dit dans quel sens le fuseau automatique revient.
      const turned = write.result.corridor?.orientation;
      const inDirection = !zone && turned?.applied ? t("Dans le sens {cap}", { cap: capLabel(turned.bearing_deg) }) : undefined;
      notify({
        text: zone ? t("Fuseau du terrain rétabli") : t("Fuseau automatique rétabli"),
        detail: withOrders(inDirection, {
          ...write.result,
          notices: (write.result.notices ?? []).filter((n) => n !== CORRIDOR_RESET_NOTICE),
        }),
        tone: "info",
        ...orderActions(undoAction(recorded), write.result),
      });
    },
    onError: (error) => writeFailure(queryClient, error, t("Fuseau non enregistré")),
  });

  // Mission d'une unité à un temps (menu de mission, §2.5) : aucune autre unité ne change de rôle ni de mission.
  const setMission = useMutation({
    mutationFn: (choice: MissionChoice) =>
      dossierWrite(queryClient, (target) => api.setMission(target, choice), {
        notSaved: t("Enregistrez le dossier pour changer la mission."),
        channel: "dispositif",
      }),
    onSuccess: (write, choice) => {
      const unit = write.result.units.find((u) => u.key === choice.unit);
      const designation = unit?.designation ?? t("Unité");
      const recorded = recordWrite(write, t("mission de {unit} en {phase}", { unit: designation, phase: choice.phase }));
      const doctrinal = unit?.positions[choice.phase]?.doctrinal_mission;
      const mission =
        choice.mission === ""
          ? t("Sans mission")
          : choice.mission === null
            ? doctrinal
              ? t("mission doctrinale ({mission})", { mission: unitMissionName(doctrinal) })
              : t("mission doctrinale")
            : unitMissionName(choice.mission);
      const edited = write.result.missions_edited ?? [];
      // Placement selon la mission non obtenu, notes nouvelles du serveur : dans le détail, l'avis passe en info.
      const notes = missionChoiceNotes(write, choice);
      const detail = [notes, edited.length ? t("Le §3.b a été modifié à la main.") : undefined].filter(Boolean).join(" ");
      // Propagation (docs/25 §6) : l'avis récapitulatif remplace l'avis ordinaire (cause → effets, Annuler tout).
      // Une mission d'une autre posture laisse l'unité en place à dessein : pas de note « n'a pas pu être placée ».
      if (showPropagation(write, recorded, edited.length ? t("Le §3.b a été modifié à la main.") : undefined)) {
        notify(null);
        return;
      }
      notify({
        text: t("Mission de {unit} en {phase} : {mission}", { unit: designation, phase: choice.phase, mission }),
        detail: withOrders(detail || undefined, write.result),
        tone: notes ? "info" : "ok",
        ...orderActions(undoAction(recorded), write.result),
      });
    },
    onError: (error) => writeFailure(queryClient, error, t("Mission non enregistrée")),
  });

  // Sens de la manœuvre (docs/14 §2.14) : une seule entrée d'historique, dispositif et graphiques compris.
  const setOrientation = useMutation({
    mutationFn: (input: OrientationWrite) => writeOrientation(queryClient, input),
    onSuccess: (result) => {
      const { write, graphics } = result;
      const parts =
        graphics.write && graphics.write.before != null && graphics.write.after != null
          ? [{ channel: "graphiques" as const, before: graphics.write.before, after: graphics.write.after }]
          : [];
      const changed =
        (write.before != null && write.after != null && !sameState(write.before, write.after)) ||
        parts.some((part) => !sameState(part.before, part.after));
      if (changed && write.before != null && write.after != null) {
        mapHistory.recordMany(
          { channel: "dispositif", before: write.before, after: write.after },
          parts,
          t("sens de la manœuvre ({cap})", { cap: formatBearing(result.bearing) }),
        );
      }
      const restored = snapToTemplate(result.bearing, result.template) === result.template;
      const title = restored
        ? t("Sens par défaut rétabli : {cap}", { cap: capLabel(result.template) })
        : t("Sens de la manœuvre : {cap}", { cap: capLabel(result.bearing) });
      if (graphics.error && graphics.rotated) {
        // Dispositif orienté, graphiques refusés : l'historique ne garde que le dispositif ; « Réessayer » renvoie les
        // graphiques tournés calculés à l'appui.
        const rotated = graphics.rotated;
        notify({
          text: t("Graphiques non tournés"),
          detail: [title, graphics.error instanceof ApiError ? tServer(graphics.error.message) : t("Serveur injoignable")].join(" · "),
          tone: "warn",
          action: {
            label: t("Réessayer"),
            run: () =>
              void dossierWrite(queryClient, (target, opo) => api.setGraphics(target, rotated, opo), {
                notSaved: t("Dossier non enregistré"),
                channel: "graphiques",
                orders: false,
              })
                .then((retry) => {
                  recordWrite(retry, t("rotation des graphiques"));
                  notify({ text: t("{n} graphiques tournés", { n: graphicsCount(rotated) }), tone: "ok" });
                })
                .catch((error: unknown) => writeFailure(queryClient, error, t("Graphiques non tournés"))),
          },
        });
        return;
      }
      const detail = [
        result.custom ? t("Fuseau retouché tourné d'un bloc") : t("Fuseau automatique recalé sur le terrain"),
        shiftPhrase(result.shift),
        graphics.write ? t("{n} graphiques tournés", { n: graphicsCount(graphicsOf(graphics.write.result)) }) : "",
      ].filter(Boolean);
      notify({
        text: title,
        detail: withOrders(detail.join(" · ") || undefined, write.result),
        tone: "ok",
        ...orderActions(undoAction(changed), write.result),
      });
    },
    onError: (error) => {
      if (isConflict(error)) {
        writeFailure(queryClient, error, t("Sens de la manœuvre non enregistré"));
        return;
      }
      notify({
        text: t("Sens de la manœuvre non enregistré"),
        detail: error instanceof ApiError ? tServer(error.message) : t("Serveur injoignable"),
        tone: "warn",
      });
    },
  });

  return {
    /** Dossier enregistré, brouillon non modifié : les écritures sont possibles (sinon `ui.requestSave`). */
    canEdit: Boolean(params?.exercise_id) && !locked,
    setCorridor,
    resetCorridor,
    setMission,
    setOrientation,
    /** Écriture en cours, annulations / rétablissements de l'historique et lignes de la ZO compris (en attente ou en
     *  vol, glissement de ligne compris). */
    busy:
      historyBusy ||
      measureBusy ||
      setCorridor.isPending ||
      resetCorridor.isPending ||
      setMission.isPending ||
      setOrientation.isPending,
  };
}
